import { Alert } from '@/components/ui';
import { useMeta } from '@/lib/api/meta';
import { formatClockTime } from '@/lib/format';

/**
 * "We are not printing right now", and what that means for the delivery date.
 *
 * SHOWN ONLY OUTSIDE PRINTING HOURS. During the day it says nothing: a notice
 * that is always on screen is one nobody reads, and "we print 7am-10pm" is
 * not news to someone ordering at two in the afternoon.
 *
 * The two outside-hours cases are genuinely different and the copy says so.
 * Before opening, the order is printed when the shutters go up and still goes
 * out today. After closing, it waits for tomorrow's run. Telling the early
 * customer "tomorrow" would be wrong, and the delivery date they are shown
 * already knows the difference — see `delivery.same_day_is_possible`.
 */

export function PrintingHoursNotice() {
  const { data: meta } = useMeta();
  if (!meta || meta.is_printing_now) return null;

  const opens = formatClockTime(meta.printing_opens_at);
  const closes = formatClockTime(meta.printing_closes_at);
  if (!opens || !closes) return null;

  // Which side of the day we are on. The server already decided we are
  // outside the hours; this only picks the sentence.
  const now = new Date();
  const [openHour, openMinute] = meta.printing_opens_at.split(':').map(Number);
  const beforeOpening =
    now.getHours() < (openHour ?? 0) ||
    (now.getHours() === openHour && now.getMinutes() < (openMinute ?? 0));

  return (
    <Alert severity="info" title={`We print from ${opens} to ${closes}`}>
      <p>
        {beforeOpening
          ? `Nobody is at the press just yet. Your order is printed as soon as we open at ${opens}, and the delivery date below already allows for it.`
          : `We have finished printing for today. Your order goes out with tomorrow morning's run, and the delivery date below already allows for it.`}
      </p>
    </Alert>
  );
}
